import { Link } from '@tanstack/react-router'
import { useEnumsQuery } from '@/api/accommodation/hooks'
import { trackHomeStayTypeClicked } from '@/lib/track'

// Unsplash images mapped by common Czech stay-type label name keywords.
// More specific / longer keywords must come before shorter ones that could also match.
const LABEL_IMAGES: Record<string, string> = {
  // Samota v přírodě — isolated Nordic/forest cabin
  samot: 'https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?w=600&h=600&q=70&auto=format&fit=crop',
  // Hory a příroda — green mountain across body of water
  hor: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&h=600&q=70&auto=format&fit=crop',
  // Krb a atmosféra — cozy indoor fireplace
  krb: 'https://images.unsplash.com/photo-1543393470-b2c833b98dce?w=600&h=600&q=70&auto=format&fit=crop',
  atmosfér: 'https://images.unsplash.com/photo-1543393470-b2c833b98dce?w=600&h=600&q=70&auto=format&fit=crop',
  // U vody — lake and trees
  vod: 'https://images.unsplash.com/photo-1557456170-0cf4f4d0d362?w=600&h=600&q=70&auto=format&fit=crop',
  jezero: 'https://images.unsplash.com/photo-1557456170-0cf4f4d0d362?w=600&h=600&q=70&auto=format&fit=crop',
  řek: 'https://images.unsplash.com/photo-1557456170-0cf4f4d0d362?w=600&h=600&q=70&auto=format&fit=crop',
  // Krásný výhled — aerial mountain panorama
  výhled: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=600&q=70&auto=format&fit=crop',
  // Příroda (generic) — Krkonoše National Park pine forest
  příroda: 'https://images.unsplash.com/photo-1549305806-c8a836ab3f5c?w=600&h=600&q=70&auto=format&fit=crop',
  les: 'https://images.unsplash.com/photo-1549305806-c8a836ab3f5c?w=600&h=600&q=70&auto=format&fit=crop',
  // Pro partu — people having a bonfire
  part: 'https://images.unsplash.com/photo-1475483768296-6163e08872a1?w=600&h=600&q=70&auto=format&fit=crop',
  kamar: 'https://images.unsplash.com/photo-1475483768296-6163e08872a1?w=600&h=600&q=70&auto=format&fit=crop',
  romantik: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=600&h=600&q=70&auto=format&fit=crop',
  dvou: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=600&h=600&q=70&auto=format&fit=crop',
  rodin: 'https://images.unsplash.com/photo-1476703993599-0035a21b17a9?w=600&h=600&q=70&auto=format&fit=crop',
  děti: 'https://images.unsplash.com/photo-1476703993599-0035a21b17a9?w=600&h=600&q=70&auto=format&fit=crop',
  mazlíček: 'https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=600&h=600&q=70&auto=format&fit=crop',
  psi: 'https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=600&h=600&q=70&auto=format&fit=crop',
  wellness: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&h=600&q=70&auto=format&fit=crop',
  relaxa: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&h=600&q=70&auto=format&fit=crop',
  homeoffice: 'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=600&h=600&q=70&auto=format&fit=crop',
  prác: 'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=600&h=600&q=70&auto=format&fit=crop',
  chalup: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=600&h=600&q=70&auto=format&fit=crop',
  babičk: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=600&h=600&q=70&auto=format&fit=crop',
  turistik: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&h=600&q=70&auto=format&fit=crop',
  bezbariér: 'https://images.unsplash.com/photo-1541199249251-f713e6145474?w=600&h=600&q=70&auto=format&fit=crop',
  dobrodružstv: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&h=600&q=70&auto=format&fit=crop',
}

// Czech/mountain themed fallbacks
const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1577138565420-9780b78b0ee1?w=600&h=600&q=70&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1559154021-0838d6102c62?w=600&h=600&q=70&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1510798831971-661eb04b3739?w=600&h=600&q=70&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=600&q=70&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1540202404-a2f29d0f9e5f?w=600&h=600&q=70&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=600&q=70&auto=format&fit=crop',
] as const

function getImageForLabel(name: string, index: number): string {
  const lower = name.toLowerCase()
  for (const [keyword, url] of Object.entries(LABEL_IMAGES)) {
    if (lower.includes(keyword)) return url
  }
  return FALLBACK_IMAGES[index % FALLBACK_IMAGES.length] ?? FALLBACK_IMAGES[0]
}

export function StayTypeCards() {
  const { data: enums } = useEnumsQuery()
  const labels = enums?.labels ?? []

  if (labels.length === 0) return null

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
      {labels.map((label, index) => (
        <Link
          key={label.id}
          to="/ubytovani/hledat"
          search={{
            labelId: label.id,
            beds: 1,
            cribs: 0,
            region: 'all',
            adult: false,
            pets: false,
            skip: 0,
            take: 12,
            order: 'start',
            direction: 'asc',
          }}
          onClick={() => trackHomeStayTypeClicked(label.id, label.name)}
          className="group relative aspect-square cursor-pointer overflow-clip rounded-xl transition-opacity hover:opacity-90"
        >
          <img
            src={getImageForLabel(label.name, index)}
            alt={label.name}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-linear-to-b from-transparent from-50% to-[rgba(30,30,30,0.7)] to-90%" />
          <div className="absolute inset-0 flex flex-col justify-end p-3">
            <div className="text-sm font-semibold text-white sm:text-base">{label.name}</div>
          </div>
        </Link>
      ))}
    </div>
  )
}
